/**
 * formatText.js
 * Shared text formatting utility
 * 
 * Supports:
 *   *bold*   → <strong>bold</strong>
 *   _italic_ → <em>italic</em>
 * 
 * Usage:
 *   import { formatInline } from '../utils/formatText'
 *   v-html="formatInline(message.content)"
 * 
 * This only handles inline bold/italic.
 * PostCard has its own fuller formatContent() that also does
 * URLs, YouTube embeds, @mentions, emojis, etc.
 * PostCard should call formatInline() inside its own formatContent().
 */

/**
 * Escape HTML to prevent XSS
 */
export function escapeHtml(text) {
  if (!text) return ''
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Apply inline formatting: *bold* and _italic_
 * Call this AFTER HTML escaping but BEFORE converting line breaks
 */
export function applyInlineFormatting(html) {
  if (!html) return ''

  let formatted = html

  // Bold: *text* (not inside URLs or HTML attributes)
  // Two-pass: multi-word then single-word
  formatted = formatted.replace(/(?<!\w)\*([^\s*][^*]*[^\s*])\*(?!\w)/g, '<strong>$1</strong>')
  formatted = formatted.replace(/(?<!\w)\*([^\s*]+)\*(?!\w)/g, '<strong>$1</strong>')

  // Italic: _text_ (not inside URLs, emails, or @mentions)
  formatted = formatted.replace(/(?<![a-zA-Z0-9@/])_([^\s_][^_]*[^\s_])_(?![a-zA-Z0-9])/g, '<em>$1</em>')
  formatted = formatted.replace(/(?<![a-zA-Z0-9@/])_([^\s_]+)_(?![a-zA-Z0-9])/g, '<em>$1</em>')

  return formatted
}

/**
 * Simple inline formatter for chat messages and other plain text.
 * Escapes HTML, applies bold/italic, converts line breaks.
 */
export function formatInline(text) {
  if (!text) return ''

  let formatted = escapeHtml(text)
  formatted = applyInlineFormatting(formatted)
  formatted = formatted.replace(/\n/g, '<br>')

  return formatted
}